import * as signalR from '@microsoft/signalr';
import vueAppSubfolder from '../utils/appSubfolderUtils';
import jwtDecode from 'jwt-decode';
import httpClientService from '../services/httpClientService';

// How long to wait before starting over once automatic reconnect has given up.
const RESTART_DELAY_MS = 30 * 1000;

class SignalRService {
	constructor() {
		this.connection = null;
		this.callbacks = [];
		this.restartTimer = null;
	}

	async startConnection() {
		let connection = null;
		try {
			let hubUrl =
				(vueAppSubfolder.get() === '/' ? '' : vueAppSubfolder.get()) +
				'/NotificationsModule/NotificationHub';
			let accessToken = localStorage.getItem('bearer');
			if (!accessToken) {
				return false;
			}
			let decodedJWT = jwtDecode(accessToken);
			let userId = decodedJWT.nameid;

			let isWindowsLogin = localStorage.getItem('last_login_type') === '2';
			let connectionOptions = {};

			if (isWindowsLogin) {
				// Add header for Windows auth to ensure server uses WindowsScheme
				connectionOptions.headers = { 'X-Use-Windows-Auth': 'true' };
			} else {
				// Called on every connect and reconnect; a tab left idle past expiry would otherwise
				// keep presenting the expired token and every reconnect would get a 403.
				connectionOptions.accessTokenFactory = () =>
					httpClientService.GetValidAccessToken();
			}

			connection = new signalR.HubConnectionBuilder()
				.withUrl(hubUrl, connectionOptions)
				.withAutomaticReconnect()
				.build();
			this.connection = connection;

			// Set up the notification handler
			connection.on('ReceiveNotification', (message) => {
				this.callbacks.forEach((callback) => callback(message));
			});

			// A reconnect gets a new connection id, which is not in the user's group until registered again.
			connection.onreconnected(() =>
				connection.invoke('RegisterUser', userId).catch(() => {})
			);

			// Automatic reconnect gave up (e.g. the server was down longer than its retries): start over.
			// Not after stopConnection, which clears this.connection first.
			connection.onclose(() => {
				if (this.connection === connection) {
					this.scheduleRestart();
				}
			});

			await connection.start();

			// Register the user with the hub
			await connection.invoke('RegisterUser', userId);

			return true;
		} catch (error) {
			if (connection && this.connection === connection) {
				this.scheduleRestart();
			}
			return false;
		}
	}

	scheduleRestart() {
		clearTimeout(this.restartTimer);
		this.restartTimer = setTimeout(() => {
			this.restartTimer = null;
			if (this.connection) {
				this.connection.stop().catch(() => {});
				this.startConnection();
			}
		}, RESTART_DELAY_MS);
	}

	onNotification(callback) {
		this.callbacks.push(callback);
	}

	async stopConnection() {
		clearTimeout(this.restartTimer);
		this.restartTimer = null;
		const connection = this.connection;
		this.connection = null;
		this.callbacks = [];
		if (connection) {
			await connection.stop();
		}
	}
}

export default new SignalRService();
